import { useIsMobile } from "@shared/widgets/hooks/useIsMobile";
import {
    formatCurrency,
    getTokenMetadataSync,
    type SelectOption,
    useWallet
} from "@solomon-apps/shared";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";
import {
    ArrowShortUpRightIcon,
    CheckIcon,
    CrossIcon
} from "@solomon-labs/design/components/icons";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    ButtonIcon,
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerTitle,
    Text
} from "@solomon-labs/design/components/ui";
import type { JupiterTokenSearchResult } from "@solomon-labs/swap-client";
import * as React from "react";
import { type RefObject, useCallback, useMemo, useRef, useState } from "react";

import {
    getNativeSolUiAmount,
    jupiterLogoUrl,
    useJupiterHoldings,
    useJupiterTokenSearch,
    useJupiterTokenSearchBatch,
    useJupiterVerifiedMints
} from "@/hooks/swap";

interface TokenSelectorProps {
    options: SelectOption[];
    excludedMints?: readonly string[];
    value: string;
    onChange: (value: string) => void;
    isOpen: boolean;
    onToggle: () => void;
    triggerRef?: RefObject<HTMLDivElement>;
    onSelectOption?: (payload: { option: SelectOption }) => void;
}

const SOL_MINT = WELL_KNOWN_TOKENS_STRINGS.SOL;

interface HoldingBalance {
    mint: string;
    balance: number;
}

function resolveTokenMetadata(
    mint: string,
    remoteMetadata: JupiterTokenSearchResult | undefined
): { label: string; icon?: string; usdPrice: number | null } {
    const localMetadata = getTokenMetadataSync(mint);

    return {
        label:
            remoteMetadata?.symbol ||
            remoteMetadata?.name ||
            localMetadata?.symbol ||
            localMetadata?.name ||
            mint.slice(0, 8),
        icon: jupiterLogoUrl(remoteMetadata?.icon || localMetadata?.icon),
        usdPrice:
            typeof remoteMetadata?.usdPrice === "number" &&
            Number.isFinite(remoteMetadata.usdPrice)
                ? remoteMetadata.usdPrice
                : null
    };
}

export function TokenSelector({
    options,
    excludedMints = [],
    onChange,
    isOpen,
    onToggle,
    onSelectOption
}: TokenSelectorProps): React.ReactElement {
    const { connected, publicKey } = useWallet();
    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isMobile = useIsMobile();

    const walletAddress = publicKey?.toBase58() || null;
    const { data: holdings } = useJupiterHoldings(walletAddress);

    const positiveHoldings = useMemo(() => {
        if (!holdings) return [];

        const balances: HoldingBalance[] = [];

        Object.entries(holdings.tokens || {}).forEach(([mint, accounts]) => {
            const balance = accounts.reduce(
                (sum, account) => sum + (account.uiAmount ?? 0),
                0
            );
            if (balance > 0) {
                balances.push({ mint, balance });
            }
        });

        // Native SOL is reported at the top level of the Ultra payload;
        // `native` only mirrors it and can be absent, which would drop SOL
        // from the list entirely for a wallet that holds it.
        const nativeSol = getNativeSolUiAmount(holdings);
        if (nativeSol !== null && nativeSol > 0) {
            balances.push({
                mint: SOL_MINT,
                balance: nativeSol
            });
        }

        return balances.filter(({ mint }) => !excludedMints.includes(mint));
    }, [excludedMints, holdings]);

    const balanceByMint = useMemo(() => {
        const map = new Map<string, number>();
        positiveHoldings.forEach(({ mint, balance }) => map.set(mint, balance));
        return map;
    }, [positiveHoldings]);

    const positiveHoldingMints = useMemo(
        () => positiveHoldings.map(({ mint }) => mint),
        [positiveHoldings]
    );

    const { data: holdingsMetadata, isFetching: isHoldingsMetadataFetching } =
        useJupiterTokenSearchBatch(positiveHoldingMints);

    const getTokenBalanceValue = useCallback(
        (tokenAddress: string): number | null => {
            if (!connected) return null;
            return balanceByMint.get(tokenAddress) ?? null;
        },
        [connected, balanceByMint]
    );

    const { data: verifiedMints } = useJupiterVerifiedMints();
    const isVerified = (mint: string): boolean => !!verifiedMints?.has(mint);

    const { data: jupiterTokens, isFetching: isJupiterFetching } =
        useJupiterTokenSearch(searchQuery);

    const metadataByMint = useMemo(() => {
        const map = new Map<string, JupiterTokenSearchResult>();
        holdingsMetadata.forEach((token) => map.set(token.id, token));
        jupiterTokens?.forEach((token) => {
            if (!map.has(token.id)) map.set(token.id, token);
        });
        return map;
    }, [holdingsMetadata, jupiterTokens]);

    const jupiterOptions: SelectOption[] = useMemo(() => {
        if (!jupiterTokens) return [];
        return jupiterTokens
            .filter((token) => !excludedMints.includes(token.id))
            .map((token) => {
                const metadata = resolveTokenMetadata(token.id, token);

                return {
                    value: token.id,
                    label: metadata.label,
                    icon: metadata.icon
                };
            });
    }, [excludedMints, jupiterTokens]);

    const getTokenUsdAmount = useCallback(
        (tokenAddress: string): number | null => {
            const amount = getTokenBalanceValue(tokenAddress);
            if (amount === null || amount <= 0) return null;

            const metadata = resolveTokenMetadata(
                tokenAddress,
                metadataByMint.get(tokenAddress)
            );
            if (metadata.usdPrice === null) return null;
            return amount * metadata.usdPrice;
        },
        [getTokenBalanceValue, metadataByMint]
    );

    const getTokenBalance = (tokenAddress: string): string => {
        const amount = getTokenBalanceValue(tokenAddress);
        if (amount === null || amount <= 0) return "-";
        const fractionDigits = isMobile ? 2 : 4;
        return amount.toLocaleString("en-US", {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        });
    };

    const localCombined = useMemo(() => {
        const combined = options.filter(
            (option) => !excludedMints.includes(option.value)
        );

        positiveHoldings.forEach(({ mint }) => {
            if (combined.find((opt) => opt.value === mint)) {
                return;
            }

            const metadata = resolveTokenMetadata(
                mint,
                metadataByMint.get(mint)
            );
            combined.push({
                value: mint,
                label: metadata.label,
                icon: metadata.icon
            });
        });

        return combined.sort((a, b) => {
            const aUsd = getTokenUsdAmount(a.value);
            const bUsd = getTokenUsdAmount(b.value);

            if (aUsd !== null && bUsd !== null) return bUsd - aUsd;
            if (aUsd !== null) return -1;
            if (bUsd !== null) return 1;
            return 0;
        });
    }, [
        options,
        positiveHoldings,
        metadataByMint,
        excludedMints,
        getTokenUsdAmount
    ]);

    const optionsToRender = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (q.length === 0) return localCombined;

        const localMatches = localCombined.filter(
            (opt) =>
                opt.label?.toLowerCase().includes(q) ||
                opt.value.toLowerCase().includes(q)
        );

        const seen = new Set(localMatches.map((o) => o.value));
        const remoteMatches = jupiterOptions.filter((o) => !seen.has(o.value));

        return [...localMatches, ...remoteMatches];
    }, [searchQuery, jupiterOptions, localCombined]);

    const handleSelect = (option: SelectOption): void => {
        onChange(option.value);
        onToggle();
        onSelectOption?.({ option });
    };

    const commandBody = (
        <Command shouldFilter={false}>
            <div className="mx-4 h-px bg-line-neutral" />

            <div className="w-full px-4 py-3 [&_[data-slot=command-input-wrapper]]:border [&_[data-slot=command-input-wrapper]]:border-line-base [&_[data-slot=command-input-wrapper]]:focus-within:border-accent">
                <CommandInput
                    ref={searchInputRef}
                    placeholder="Enter token or address..."
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    className="h-full w-full min-w-0 rounded-none border-0 bg-transparent p-0 font-sans text-base text-foreground-base placeholder:text-foreground-neutral"
                />
            </div>

            <div className="h-px bg-line-neutral" />

            <CommandList className="w-full h-[385px]">
                <CommandEmpty className="flex items-center justify-center py-10">
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-neutral"
                    >
                        {isJupiterFetching ? "Searching…" : "No tokens found"}
                    </Text>
                </CommandEmpty>

                {optionsToRender.map((option) => {
                    const balance = getTokenBalance(option.value);
                    const usdAmount = getTokenUsdAmount(option.value);
                    const usdValue =
                        usdAmount === null ? "-" : formatCurrency(usdAmount);

                    return (
                        <CommandItem
                            key={option.value}
                            value={option.value}
                            onSelect={() => handleSelect(option)}
                            className="group/item relative flex w-full items-center justify-between px-4 py-3 cursor-pointer overflow-hidden border-b border-line-neutral"
                        >
                            <span className="absolute top-0 left-0 h-px bg-accent w-0 group-data-[selected=true]/item:w-full transition-all duration-300 ease-out z-10" />
                            <span className="absolute inset-x-0 top-0 h-0 group-data-[selected=true]/item:h-full transition-all duration-300 ease-out z-0 bg-line-neutral" />

                            <div className="relative z-10 flex flex-row items-center gap-2">
                                <Avatar
                                    size="default"
                                    className="size-8 border-0 bg-transparent"
                                >
                                    {option.icon && (
                                        <AvatarImage
                                            src={option.icon}
                                            alt={option.label || "Token"}
                                        />
                                    )}
                                    <AvatarFallback className="border border-line-base">
                                        {(option.label || option.value)
                                            .charAt(0)
                                            .toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>

                                <div className="flex flex-col">
                                    <div className="flex flex-row items-center gap-1">
                                        <Text
                                            variant="body"
                                            render={<span />}
                                            className="text-foreground-base"
                                        >
                                            {option.label ||
                                                option.value.slice(0, 8)}
                                        </Text>
                                        {isVerified(option.value) && (
                                            <CheckIcon className="text-primary-base size-3" />
                                        )}
                                    </div>

                                    <a
                                        href={`https://solscan.io/token/${option.value}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`View ${option.label || option.value} on Solscan`}
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                        onKeyDown={(event) =>
                                            event.stopPropagation()
                                        }
                                        className="flex flex-row items-center gap-1 text-foreground-neutral hover:text-foreground-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                                    >
                                        <Text variant="small" render={<span />}>
                                            {option.value.slice(0, 4)}
                                            ...
                                            {option.value.slice(-4)}
                                        </Text>
                                        {/* Keep the address arrow smaller than CommandItem's default icons. */}
                                        <ArrowShortUpRightIcon className="size-3.5!" />
                                    </a>
                                </div>
                            </div>

                            {balance !== "-" && (
                                <div className="relative z-10 ml-auto flex shrink-0 flex-col items-end text-right tabular-nums">
                                    <Text
                                        variant="number"
                                        className="text-foreground-base"
                                    >
                                        {balance}
                                    </Text>
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="text-foreground-neutral"
                                    >
                                        {usdValue}
                                    </Text>
                                </div>
                            )}
                        </CommandItem>
                    );
                })}
            </CommandList>
        </Command>
    );

    if (isMobile) {
        return (
            <Drawer
                open={isOpen}
                onOpenChange={(v): void => {
                    if (!v) onToggle();
                }}
            >
                <DrawerContent
                    aria-label="Select token"
                    className="max-h-[85dvh] border-t-2 border-accent gap-0"
                >
                    <div className="flex items-center justify-between px-4 pb-2 pt-1">
                        <DrawerTitle>Select token</DrawerTitle>
                        <DrawerClose
                            render={
                                <ButtonIcon
                                    variant="transparent"
                                    size="xl"
                                    className="p-1"
                                    aria-label="Close"
                                    icon={<CrossIcon />}
                                />
                            }
                        />
                    </div>
                    {commandBody}
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(v): void => {
                if (!v) onToggle();
            }}
        >
            <DialogContent
                initialFocus={searchInputRef}
                status={
                    isJupiterFetching || isHoldingsMetadataFetching
                        ? "pending"
                        : "idle"
                }
                className="max-w-[448px] border-2 border-line-base border-t-0 flex flex-col overflow-hidden gap-0 pt-4"
            >
                <div className="flex items-center justify-between px-4 pb-2">
                    <DialogTitle>Select token</DialogTitle>
                    <DialogClose
                        render={
                            <ButtonIcon
                                variant="transparent"
                                size="xl"
                                className="p-1"
                                aria-label="Close"
                                icon={<CrossIcon />}
                            />
                        }
                    />
                </div>
                {commandBody}
            </DialogContent>
        </Dialog>
    );
}
